import React, { useState } from 'react';
import { Button } from '../ui/button';
import { GitHubLogoIcon, ExternalLinkIcon, Pencil1Icon } from '@radix-ui/react-icons';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { SourceCodeLinkingForm } from './SourceCodeLinkingForm';

interface ViewSourceCodeButtonProps {
    serviceName: string;
    sourceCodeUrl: string;
    repositoryUrl?: string;
    servicePath?: string;
}

export const ViewSourceCodeButton: React.FC<ViewSourceCodeButtonProps> = ({ serviceName, sourceCodeUrl, repositoryUrl, servicePath }) => {
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    
    const handleViewSourceCode = () => {
        // Open repository URL in new tab
        if (sourceCodeUrl) {
            // Ensure URL has https:// protocol
            const urlToOpen = sourceCodeUrl.startsWith('http://') || sourceCodeUrl.startsWith('https://') 
                ? sourceCodeUrl 
                : `https://${sourceCodeUrl}`;
            window.open(urlToOpen, '_blank');
        }
    };
    
    const handleEditSourceCode = () => {
        setEditDialogOpen(true);
    };
    
    const handleEditSuccess = () => {
        setEditDialogOpen(false);
        // Refresh the page to update the button state
        window.location.reload();
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 border border-border rounded"
                    >
                        <GitHubLogoIcon className="h-4 w-4" />
                        Source Code
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="text-textmedium bg-backgrounddark rounded">
                    <DropdownMenuItem
                        onClick={handleViewSourceCode}
                        className="hover:text-textlight hover:bg-backgroundlight cursor-pointer"
                    >
                        <ExternalLinkIcon className="h-4 w-4 mr-2" />
                        Open
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={handleEditSourceCode}
                        className="hover:text-textlight hover:bg-backgroundlight cursor-pointer"
                        requiredPermission={{
                            operation: "update",
                            resource: "integrations/default/*"
                        }}
                    >
                        <Pencil1Icon className="h-4 w-4 mr-2" />
                        Edit
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="sm:max-w-[500px] text-textmedium">
                    <DialogHeader>
                        <DialogTitle className={"mb-2"}>Edit Source Code Repository</DialogTitle>
                        <DialogDescription className={"text-textdark"}>
                            Update the source code repository settings for this service.
                        </DialogDescription>
                    </DialogHeader>
                    <SourceCodeLinkingForm 
                        serviceName={serviceName}
                        editMode={true}
                        currentRepositoryUrl={repositoryUrl || sourceCodeUrl}
                        currentServicePath={servicePath}
                        onSuccess={handleEditSuccess}
                        onCancel={() => setEditDialogOpen(false)}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};